import api, { currentUser } from './api';
import { isRecordKeeper, isSalaryKeeper } from './recordOverride';

/** Same role codes and groups as the server (backend/core/rights.py). */
export const ROLE_LABELS = {
  ADMIN: 'Admin',
  MANAGER: 'General manager',
  SCHOOL_MANAGER: 'School manager',
  LIVERY_MANAGER: 'Stable & livery manager',
  STABLE_MANAGER: 'Stable & livery manager',
  CASHIER: 'Cashier',
  ACCOUNTANT: 'Accountant',
  COACH: 'Coach',
  SECURITY: 'Security',
  DATAENTRY: 'Store / data entry',
  OFFICE: 'General user',
  FARRIER: 'Farrier / yard staff',
  PURCHASER: 'Purchase manager',
  HEAD_GROOM: 'Head of grooms',
};

export const GM = ['MANAGER'];
export const SCHOOL_MGRS = ['MANAGER', 'SCHOOL_MANAGER'];
export const LIVERY_MGRS = ['MANAGER', 'LIVERY_MANAGER'];
export const MGMT = ['MANAGER', 'SCHOOL_MANAGER', 'LIVERY_MANAGER'];
export const FINANCE = ['MANAGER', 'ACCOUNTANT'];
export const NO_MONEY = ['SECURITY', 'COACH', 'FARRIER', 'HEAD_GROOM'];

/** Main role plus extra roles of the signed-in user. */
export const userRoles = (user = currentUser()) => {
  if (!user) return [];
  return user.roles && user.roles.length ? user.roles : [user.role].filter(Boolean);
};

export const hasRole = (...roles) => {
  const want = roles.flat();
  return userRoles().some((r) => want.includes(r));
};

export const isAdmin = () => hasRole('ADMIN');

/** Admin passes every role check, like on the server. */
export const can = (...roles) => isAdmin() || hasRole(...roles);

export const seesMoney = () => {
  const mine = userRoles();
  return !mine.length || !mine.every((r) => NO_MONEY.includes(r));
};

export const roleLabel = (user = currentUser()) => {
  if (!user) return '';
  const labels = [user.role, ...userRoles(user)].map((r) => ROLE_LABELS[r] || r);
  return [...new Set(labels)].join(' + ');
};

const LANDING = {
  ADMIN: '/home',
  MANAGER: '/home',
  SCHOOL_MANAGER: '/home',
  LIVERY_MANAGER: '/home',
  STABLE_MANAGER: '/home',
  CASHIER: '/school/day-end',
  ACCOUNTANT: '/home',
  COACH: '/coach-day',
  SECURITY: '/gate',
  DATAENTRY: '/home',
  OFFICE: '/home',
  FARRIER: '/service-records',
  PURCHASER: '/home',
  HEAD_GROOM: '/feed-give',
};

/** The first screen of the user's main role. */
export const landingPath = () => LANDING[currentUser()?.role] || '/home';

/* Screens & tabs the Admin allows per role (Screens & Roles). Keys: menu path, tabs "path#tab". */
const SCREENS_KEY = 'screens';
const MENU_KEY = 'menu';

export const loadScreens = async () => {
  const r = await api.get('/auth/screens/');
  localStorage.setItem(SCREENS_KEY, JSON.stringify(r.data.hidden || []));
  if (Array.isArray(r.data.menu)) {
    localStorage.setItem(MENU_KEY, JSON.stringify(r.data.menu));
  }
  return r.data;
};

/** Menu groups from the server (Screens registry). null until loaded. */
export const serverMenu = () => {
  try {
    const m = JSON.parse(localStorage.getItem(MENU_KEY) || 'null');
    return Array.isArray(m) ? m : null;
  } catch {
    return null;
  }
};

const hiddenScreens = () => {
  try {
    return JSON.parse(localStorage.getItem(SCREENS_KEY) || 'null');
  } catch {
    return null;
  }
};

/** null until the list is loaded from the server. */
export const screensLoaded = () => Array.isArray(hiddenScreens());

/** Retired screens: never shown, even to Admin (same list as backend NEVER_SHOW). */
const NEVER_SHOW = ['/horse-activities', '/coach-pnl'];
const KEEPER_ONLY = ['/open-close-period', '/school/opening'];
const COST_KEEPER_ONLY = ['/cost-centres'];

const SECTION_KEY = {
  '/horse-health': { due: 'due', routine: 'routine', medical: 'medical', incidents: 'incidents' },
  '/yard-stock': {
    feed: 'feed', hay: 'hay', bedding: 'bedding', room: 'room', farrier: 'farrier', medicine: 'medicine',
  },
  '/bedding': { day: 'day', plan: 'plan', exceptions: 'exceptions' },
  '/livery': {
    contracts: 'contracts', invoices: 'invoices', payments: 'payments', balances: 'balances',
    charges: 'charges', trainings: 'trainings', prices: 'prices', movements: 'movements',
  },
  '/horse-movements': { places: 'places', permits: 'permits', exits: 'exits' },
};

/** A menu path such as /yard-stock/feed is the screen tab /yard-stock#feed. */
export const screenKey = (key) => {
  const raw = key || '';
  if (raw.includes('#')) return raw;
  const cut = raw.indexOf('/', 1);
  if (cut < 0) return raw;
  const parent = raw.slice(0, cut);
  const tab = SECTION_KEY[parent]?.[raw.slice(cut + 1)];
  return tab ? `${parent}#${tab}` : raw;
};

export const canSee = (key) => {
  const screen = screenKey(key);
  const path = screen.split('#')[0];
  if (NEVER_SHOW.includes(path)) return false;
  if (KEEPER_ONLY.includes(path) && !isRecordKeeper()) return false;
  if (COST_KEEPER_ONLY.includes(path) && !isSalaryKeeper()) return false;
  const hidden = hiddenScreens();
  if (Array.isArray(hidden) && hidden.includes(screen)) return false;
  if (isAdmin()) return true;
  return !Array.isArray(hidden) || !hidden.includes(screen);
};

/** Tabs of a page the user may see; tab keys are "<page path>#<tab key>". */
export const visibleTabs = (page, items) => items.filter((t) => canSee(`${page}#${t.key}`));
